import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  TargetCompany,
  TargetCompanyEntry,
  TargetCompanyResponse
} from '../models/target-company';
import { CompanyContactRecord } from '../models/company-contact';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-company-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './company-list.component.html',
  styleUrl: './company-list.component.css'
})
export class CompanyListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  protected readonly serviceId = signal<number | null>(null);
  protected readonly locationId = signal<number | null>(null);
  protected readonly companies = signal<TargetCompany[]>([]);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');
  protected readonly statusMessage = signal<string>('');
  protected readonly contactLoading = signal<boolean>(false);
  protected readonly contactError = signal<string>('');
  protected readonly activeContactAction = signal<string | null>(null);
  protected readonly contactRecords = signal<CompanyContactRecord[]>([]);
  protected readonly contactRecordsLoading = signal<boolean>(false);
  protected readonly completedCompanyKeys = signal<string[]>([]);
  protected readonly companyRows = computed<CompanyRow[]>(() => {
    const currentLocationId = this.locationId();
    return this.companies().flatMap((company) => {
      const entries = this.companyEntries(company.companies);
      return entries.map((entry, index) => {
        const parsedLocationId =
          this.normaliseLocationId(entry.location_id) ??
          this.normaliseLocationId(currentLocationId);
        return {
          ...entry,
          rowKey: `${company.id}-${index}`,
          targetAudienceId: company.target_audience_id,
          targetAudienceName: company.target_audience || null,
          audienceType: company.audience_type || null,
          marketType: this.renderList(company.market_type).join(', ') || null,
          recordId: company.id,
          locationId: parsedLocationId
        };
      });
    });
  });

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const locationParam = params.get('locationId');

      const serviceId = serviceParam ? Number(serviceParam) : NaN;
      const locationId = locationParam ? Number(locationParam) : NaN;

      if (Number.isNaN(serviceId) || Number.isNaN(locationId)) {
        this.errorMessage.set('Invalid service or location identifier.');
        this.loading.set(false);
        return;
      }

      this.serviceId.set(serviceId);
      this.locationId.set(locationId);
      this.uiState.setServiceId(serviceId);
      this.uiState.setLocationId(locationId);
      this.fetchCompanies(serviceId, locationId);
      this.loadContactDetails(serviceId, locationId, { silent: true });
    });
  }

  protected goBack(): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      this.router.navigate(['/']);
      return;
    }

    this.uiState.setServiceId(serviceId);

    const currentLocationId = this.locationId();
    if (currentLocationId) {
      this.uiState.setLocationId(currentLocationId);
    }

    let audienceId = this.uiState.audienceId();
    if (!audienceId) {
      const audienceFromCompanies = this.companies()
        .map((company) => company.target_audience_id)
        .find((id) => typeof id === 'number' && !Number.isNaN(id));
      if (audienceFromCompanies != null) {
        audienceId = audienceFromCompanies;
        this.uiState.setAudienceId(audienceFromCompanies);
      }
    }

    const queryParams = audienceId ? { audienceId } : undefined;
    this.router.navigate(['/target-areas', serviceId], { queryParams });
  }

  protected refresh(): void {
    this.contactError.set('');
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (serviceId && locationId) {
      this.uiState.setServiceId(serviceId);
      this.uiState.setLocationId(locationId);
      this.fetchCompanies(serviceId, locationId);
      this.loadContactDetails(serviceId, locationId);
    }
  }

  protected generateContacts(): void {
    this.contactError.set('');
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.contactError.set('Missing identifiers to generate contact details.');
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);
    this.activeContactAction.set('all');
    this.contactLoading.set(true);
    this.statusMessage.set('Generating company contact details...');

    this.collectDataService
      .generateCompanyContactDetails(serviceId, locationId)
      .pipe(
        finalize(() => {
          if (this.activeContactAction() === 'all') {
            this.contactLoading.set(false);
            this.activeContactAction.set(null);
          }
        })
      )
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            const message =
              result?.message ||
              'Company contact generation did not complete. Please try again.';
            this.contactError.set(message);
            this.statusMessage.set('');
            return;
          }

          this.statusMessage.set(
            'Company contact details generated for all companies in this location.'
          );
          this.loadContactDetails(serviceId, locationId);
          this.markLocationContactsCompleted(locationId);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate company contacts. Please try again.';
          this.contactError.set(message);
          this.statusMessage.set('');
        }
      });
  }

  protected viewContacts(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.contactError.set('Missing identifiers to display contact details.');
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);
    this.contactError.set('');
    this.router.navigate(['/contacts', serviceId, locationId]);
  }

  protected generateContactsForCompany(row: CompanyRow): void {
    const serviceId = this.serviceId();
    const resolvedLocationId =
      this.normaliseLocationId(row.locationId) ?? this.locationId();
    if (!serviceId || !resolvedLocationId) {
      this.contactError.set(
        'Missing identifiers to generate contact details for this company.'
      );
      return;
    }

    const targetName = row.name?.trim() || null;
    const trimmedWebsite = row.website?.trim() || null;
    const targetWebsite =
      trimmedWebsite && trimmedWebsite.toLowerCase() !== 'n/a'
        ? trimmedWebsite
        : null;

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(resolvedLocationId);
    this.activeContactAction.set(row.rowKey);
    this.statusMessage.set(
      `Generating contact details for ${targetName || 'the selected company'}...`
    );

    this.collectDataService
      .generateCompanyContactDetails(
        serviceId,
        resolvedLocationId,
        targetName,
        targetWebsite
      )
      .pipe(
        finalize(() => {
          if (this.activeContactAction() === row.rowKey) {
            this.contactLoading.set(false);
            this.activeContactAction.set(null);
          }
        })
      )
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            const message =
              result?.message ||
              'Company contact generation did not complete. Please try again.';
            this.contactError.set(message);
            this.statusMessage.set('');
            return;
          }

          this.statusMessage.set(
            result?.message ||
              `Company contact details generated for ${targetName || 'the selected company'}.`
          );
          this.markCompanyCompleted(row);
          this.loadContactDetails(serviceId, resolvedLocationId);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate company contacts. Please try again.';
          this.contactError.set(message);
          this.statusMessage.set('');
        }
      });
  }

  protected viewContactsForCompany(row: CompanyRow): void {
    const serviceId = this.serviceId();
    const locationId = this.normaliseLocationId(row.locationId) ?? this.locationId();
    if (!serviceId || !locationId) {
      this.contactError.set('Missing identifiers to display contact details.');
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);
    this.contactError.set('');
    this.contactLoading.set(true);

    this.collectDataService
      .getTargetCompanyAllLocations(serviceId)
      .pipe(
        switchMap(() =>
          this.collectDataService.getCompanyContactDetails(serviceId, locationId)
        ),
        finalize(() => {
          this.contactLoading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          const records = response?.data ?? [];
          const matched =
            this.findMatchingContact(records, row) ?? records[0] ?? null;

          if (!matched) {
          this.contactError.set(
              'No matching company contact record was found.'
            );
            return;
          }

          this.router.navigate([
            '/contacts',
            serviceId,
            locationId,
            matched.id
          ], {
            queryParams: { returnTo: 'company' }
          });
        },
        error: (error) => {
          this.contactError.set(this.extractErrorMessage(error));
        }
      });
  }

  protected contactRecordForRow(row: CompanyRow): CompanyContactRecord | null {
    const records = this.contactRecords();
    if (!records.length) {
      return null;
    }
    return this.findMatchingContact(records, row);
  }

  protected contactDetailsForRow(row: CompanyRow): ContactDetails | null {
    const record = this.contactRecordForRow(row);
    if (!record) {
      return null;
    }
    return {
      record,
      emails: this.renderList(record.emails),
      phones: this.renderList(record.phones)
    };
  }

  private loadContactDetails(
    serviceId: number,
    locationId: number,
    options: LoadContactOptions = {}
  ): void {
    const { silent = false } = options;
    if (!silent) {
      this.contactRecordsLoading.set(true);
    }

    this.collectDataService
      .getTargetCompanyAllLocations(serviceId)
      .pipe(
        switchMap(() =>
          this.collectDataService.getCompanyContactDetails(serviceId, locationId)
        ),
        finalize(() => {
          if (!silent) {
            this.contactRecordsLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (response) => {
          const records = response?.data ?? [];
          this.contactRecords.set(records);
          if (!silent) {
            this.contactError.set('');
          }
        },
        error: (error) => {
          const status = (error as { status?: number })?.status;
          if (status === 404) {
            this.contactRecords.set([]);
            return;
          }
          if (!silent) {
            this.contactError.set(this.extractErrorMessage(error));
          }
        }
      });
  }

  private fetchCompanies(serviceId: number, locationId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Loading company intelligence...');
    this.companies.set([]);
    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);

    this.collectDataService
      .getTargetCompanies(serviceId, locationId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (response: TargetCompanyResponse) => {
          this.companies.set(response.data);
          this.statusMessage.set(
            response.count
              ? `Showing ${response.count} company insight record(s).`
              : 'No companies found for this location.'
          );
          if (!response.count) {
            this.errorMessage.set('No companies available yet. Try regenerating from the previous page.');
          }
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to fetch company data. Please try again.';
          this.errorMessage.set(message);
          this.statusMessage.set('');
        }
      });
  }

  protected renderList(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item == null
            ? ''
            : typeof item === 'string'
            ? item
            : JSON.stringify(item)
        )
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      if (
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          return this.renderList(parsed);
        } catch {
          return [trimmed];
        }
      }

      return [trimmed];
    }

    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).map(
        ([key, val]) => `${this.formatKey(key)}: ${this.renderList(val).join(', ') || 'N/A'}`
      );
    }

    return [String(value)];
  }

  protected companyEntries(value: unknown): TargetCompanyEntry[] {
    if (!value) {
      return [];
    }

    const normalise = (entry: unknown): TargetCompanyEntry => {
      if (!entry || typeof entry !== 'object') {
        const name = String(entry ?? '').trim();
        return { name: name || null };
      }

      const record = entry as Record<string, unknown>;
      const rawLocationId =
        record['location_id'] ?? record['locationId'] ?? record['LocationId'];

      let normalisedLocationId: string | number | null = null;
      if (typeof rawLocationId === 'string' || typeof rawLocationId === 'number') {
        normalisedLocationId = rawLocationId;
      } else if (rawLocationId != null) {
        normalisedLocationId = this.coerceString(rawLocationId);
      }

      return {
        location_id: normalisedLocationId,
        name: this.coerceString(record['name']),
        industry: this.coerceString(record['industry']),
        location: this.coerceString(record['location']),
        size:
          this.coerceString(record['size']) ??
          this.coerceString(record['company_size']),
        website:
          this.coerceString(record['website']) ??
          this.coerceString(record['company_website'])
      };
    };

    if (Array.isArray(value)) {
      return value.map(normalise).filter((entry) => entry.name);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(normalise).filter((entry) => entry.name);
          }
        } catch {
          // fall through
        }
      }
      return [{ name: trimmed }];
    }

    if (typeof value === 'object') {
      return [normalise(value)];
    }

    return [];
  }

  private coerceString(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return null;
      }
    }
    return String(value);
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected trackByRow(_index: number, row: CompanyRow): string {
    return row.rowKey;
  }

  protected isContactCompleted(row: CompanyRow): boolean {
    return this.completedCompanyKeys().includes(row.rowKey);
  }

  private normaliseLocationId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (value && typeof value === 'object') {
      try {
        const parsed = Number(JSON.stringify(value));
        return Number.isNaN(parsed) ? null : parsed;
      } catch {
        return null;
      }
    }
    return null;
  }

  private markCompanyCompleted(row: CompanyRow): void {
    this.completedCompanyKeys.update((keys) =>
      keys.includes(row.rowKey) ? keys : [...keys, row.rowKey]
    );
  }

  private markLocationContactsCompleted(locationId: number): void {
    const targetLocationId = this.normaliseLocationId(locationId);
    if (targetLocationId == null) {
      return;
    }
    const locationKeys = this.companyRows()
      .filter(
        (row) =>
          this.normaliseLocationId(row.locationId) === targetLocationId
      )
      .map((row) => row.rowKey);
    if (!locationKeys.length) {
      return;
    }
    this.completedCompanyKeys.update((keys) => {
      const next = new Set(keys);
      locationKeys.forEach((key) => next.add(key));
      return Array.from(next);
    });
  }

  private findMatchingContact(
    records: CompanyContactRecord[],
    row: CompanyRow
  ): CompanyContactRecord | null {
    const targetName = (row.name || '').trim().toLowerCase();
    const targetSite = this.normaliseWebsite(row.website);

    for (const record of records) {
      const recordName = (record.company_name || '').trim().toLowerCase();
      if (targetName && recordName && recordName !== targetName) {
        continue;
      }

      const recordSite = this.normaliseWebsite(record.website);
      if (targetSite && recordSite && recordSite !== targetSite) {
        continue;
      }

      return record;
    }
    return null;
  }

  private normaliseWebsite(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'n/a') {
      return null;
    }
    try {
      const withScheme = trimmed.startsWith('http')
        ? trimmed
        : `https://${trimmed}`;
      const url = new URL(withScheme);
      const host = url.hostname.toLowerCase();
      const path = url.pathname.replace(/\/+$/, '').toLowerCase();
      return `${host}${path}`;
    } catch {
      return trimmed.replace(/\/+$/, '').toLowerCase();
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'An unexpected error occurred.';
    }
    const maybe = error as {
      message?: string;
      error?: unknown;
      detail?: string;
      statusText?: string;
    };
    const nested = maybe.error;
    if (typeof nested === 'string') {
      return nested;
    }
    if (nested && typeof nested === 'object') {
      const nestedDetail = (nested as { detail?: string }).detail;
      const nestedMessage = (nested as { message?: string }).message;
      if (nestedDetail) {
        return nestedDetail;
      }
      if (nestedMessage) {
        return nestedMessage;
      }
    }
    return (
      maybe.detail ||
      maybe.message ||
      maybe.statusText ||
      'An unexpected error occurred.'
    );
  }
}

interface CompanyRow extends TargetCompanyEntry {
  rowKey: string;
  recordId: number;
  targetAudienceId: number;
  targetAudienceName: string | null;
  audienceType: string | null;
  marketType: string | null;
  locationId: number | null;
}

interface ContactDetails {
  record: CompanyContactRecord;
  emails: string[];
  phones: string[];
}

interface LoadContactOptions {
  silent?: boolean;
}
